import React from 'react';
import { FeatureAppDefinition, FeatureAppEnvironment, FeatureServices } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import App from './FeatureApp';
import I18nContextComponent from '@oneaudi/i18n-context';
import { I18NServiceV1 } from '@oneaudi/i18n-service';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { EnvConfigServiceV1 } from '@oneaudi/audi-env-config-service';
import { EcommerceServiceV1 } from '@volkswagen-onehub/e-commerce-service';
import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import { ServerRequestV1 } from '@feature-hub/server-request';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { defineConfigurationService } from '@oneaudi/vtp-configuration-service';
import { GfaLayerManagerV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { ContentService } from '@oneaudi/content-service';
import { PageInfoServiceV1 } from '@oneaudi/page-info-service';
import { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import { AudiFootnoteServiceInterfaceV1 } from '@oneaudi/footnote-service';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import {
  createCarlineSeoResolver,
  initializeFeatureApp,
  ServicesContextProvider,
  StockContextProvider,
  FootnoteReferenceContextProvider,
  FootnoteServiceContextProvider,
  getDesignLibrary,
  DynamicGlobalStyles,
} from '@oneaudi/vtp-shared';
import { NotificationTypeV1 } from '@oneaudi/audi-notification-display-service';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { NumberFormatterServiceV1 } from '@oneaudi/number-formatter-service/dist/v1';
import { NavigationServiceV1 } from '@volkswagen-onehub/navigation-service';
import { LayerManagerV25 } from '@volkswagen-onehub/layer-manager';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 's2:async-ssr-manager': AsyncSsrManagerV1 | undefined;
  readonly 's2:server-request': ServerRequestV1 | undefined;
  readonly 's2:serialized-state-manager': SerializedStateManagerV1 | undefined;
  readonly 'gfa:locale-service': LocaleServiceV1;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
  readonly 'dbad:audi-i18n-service': I18NServiceV1;
  readonly 'gfa:layer-manager': GfaLayerManagerV1;
  readonly 'audi-content-service': ContentService;
  readonly 'audi:envConfigService': EnvConfigServiceV1;
  readonly 'page-info-service': PageInfoServiceV1;
  readonly 'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  readonly 'audi-footnote-service': AudiFootnoteServiceInterfaceV1;
  readonly 'notification-display-service'?: NotificationTypeV1;
  readonly 'audi-number-formatter-service': NumberFormatterServiceV1;
  readonly 'navigation-service'?: NavigationServiceV1;
  readonly 'e-commerce-service': EcommerceServiceV1;
  readonly 'layer-manager': LayerManagerV25;
}
export interface FeatureAppConfig {
  contextServiceUrl?: string;
  vehicleId?: string;
  contextId?: string;
  defaultURL?: string;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
      'locale-service': '1.0.0',
      'layer-manager': '^2.5.0',
      'vtp-configuration-service': '^1.0.0',
      'audi:envConfigService': '1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'audi-content-service': '^1.0.0',
      'gfa:layer-manager': '1.0.0',
      'audi-number-formatter-service': '^1.0.0',
    },
    externals: {
      react: '^16.13.0 || ^17.0.2 || ^18.2.0',
      'styled-components': '*',
      ...getDesignLibrary({
        unifiedWebCommonVersion: '^1.12.0',
        unifiedWebAlphaCommonVersion: '0.1.8',
      }),
    },
  },
  optionalDependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:server-request': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-footnote-service': '1.0.0',
      'navigation-service': '^1.3.0',
      'e-commerce-service': '^1.0.0',
      'notification-display-service': '^1.0.0',
      'gfa:layer-manager': '1.0.0',
      'page-info-service': '1.0.0',
    },
  },
  ownFeatureServiceDefinitions: [defineConfigurationService()],
  create: ({
    featureServices,
    config = {},
    baseUrl,
  }: FeatureAppEnvironment<FeatureServiceDependencies, FeatureAppConfig>) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');
    const i18nService: I18NServiceV1 = featureServices['dbad:audi-i18n-service'] as I18NServiceV1;
    const localeService: LocaleServiceV1 = featureServices['locale-service'] as LocaleServiceV1;
    const pageInfoService = featureServices['page-info-service'];
    if (config && !config?.defaultURL) {
      // eslint-disable-next-line
      config.defaultURL = `${baseUrl || ''}/static`;
    }

    // do not use destructuring here, as this breaks the ssr build
    const initialization = initializeFeatureApp<
      FeatureServiceDependencies,
      FeatureAppConfig,
      { seo: void }
    >(
      featureServices,
      config,
      {
        seo: createCarlineSeoResolver(pageInfoService, featureServices['s2:server-request']),
      },
      true
    );

    const footnoteReferenceService =
      featureServices['audi-footnote-reference-service'].getDefaultScopeRefService();

    return {
      loadingPromise: initialization.loadingPromise?.featureAppInitializationPromise,
      render: () => (
        <>
          <DynamicGlobalStyles />
          <ThemeProvider>
            <ServicesContextProvider
              contextId={initialization.contextId!}
              featureAppConfig={config}
              additionalServices={featureServices}
              envConfig={initialization.envConfig}
            >
              <I18nContextComponent
                i18nData={i18nService}
                language={localeService.language}
                // @ts-ignore
                featureServices={featureServices}
                scopes={[]}
              >
                {initialization.vehicleRaw && (
                  <StockContextProvider
                    vehicleID={initialization.vehicleId}
                    vehicle={initialization.vehicleRaw}
                    svd={initialization.svd}
                  >
                    <FootnoteServiceContextProvider
                      footnoteService={featureServices['audi-footnote-service']}
                    >
                      <FootnoteReferenceContextProvider
                        footnoteReferenceServiceScopeManager={
                          featureServices['audi-footnote-reference-service']
                        }
                      >
                        <div data-fefa-custom-id={footnoteReferenceService.getConsumerId()}>
                          {initialization.loadingPromise?.state.initialized && <App />}
                        </div>
                      </FootnoteReferenceContextProvider>
                    </FootnoteServiceContextProvider>
                  </StockContextProvider>
                )}
              </I18nContextComponent>
            </ServicesContextProvider>
          </ThemeProvider>
        </>
      ),
    };
  },
};

export default featureAppDefinition;
